'use strict';

class ICCCarousel {
  constructor(sectionEl) {
    this.section = sectionEl;
    this.sectionId = sectionEl.dataset.sectionId;
    this.stage = sectionEl.querySelector('.icc-stage');
    this.viewport = sectionEl.querySelector('.icc-viewport');

    if (!this.stage) return;

    this.realSlides = [...sectionEl.querySelectorAll('.icc-slide[data-real="true"]')];
    this.N = this.realSlides.length;
    if (this.N < 1) return;

    // Settings from data-* attributes on the section element
    this.perspective = parseInt(sectionEl.dataset.perspective) || 1200;
    this.neighborScale = parseFloat(sectionEl.dataset.neighborScale) || 0.85;
    this.neighborRotation = parseInt(sectionEl.dataset.neighborRotation) || 12;
    this.duration = parseInt(sectionEl.dataset.duration) || 500;
    this.cardGap = parseInt(sectionEl.dataset.cardGap) || 20;
    this.autoplayEnabled = sectionEl.dataset.autoplay === 'true';
    this.autoplayDelay = parseInt(sectionEl.dataset.autoplayInterval) || 4000;
    this.pauseOnHover = sectionEl.dataset.pauseOnHover === 'true';
    this.showArrows = sectionEl.dataset.showArrows !== 'false';
    this.showDots = sectionEl.dataset.showDots === 'true';

    // Desktop card width from CSS variable or data attribute
    this.desktopCardWidth = parseInt(sectionEl.dataset.desktopCardWidth) || 440;
    this.mobileCardWidthPct = parseFloat(sectionEl.dataset.mobileCardWidth) || 74;

    this.cloneCount = this.N >= 3 ? 2 : 1;
    this.currentPos = this.cloneCount; // points to first real slide
    this.isTransitioning = false;
    this.dragStartX = 0;
    this.dragDelta = 0;
    this.isDragging = false;
    this.autoplayTimer = null;
    this.isHovered = false;
    this.isUserInteracting = false;
    this.allSlides = [];
    this.dots = [];

    // Bound handlers stored for removal
    this._boundHandleResize = this._debounce(this._onResize.bind(this), 200);
    this._boundKeyDown = this._onKeyDown.bind(this);
    this._onTouchStart = this._onTouchStart.bind(this);
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd = this._onTouchEnd.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onClick = this._onClick.bind(this);

    this._init();
  }

  _init() {
    this._buildClones();
    this._setCardDimensions();
    this._positionAll(false);
    this._buildNav();
    this._buildDots();
    this._bindEvents();
    if (this.autoplayEnabled) this._startAutoplay();
  }

  _buildClones() {
    // Remove existing clones
    this.stage.querySelectorAll('.icc-slide--clone').forEach(el => el.remove());

    const clonesBefore = [];
    const clonesAfter = [];

    for (let i = this.N - 1; i >= this.N - this.cloneCount; i--) {
      const clone = this.realSlides[i].cloneNode(true);
      clone.classList.add('icc-slide--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
      clonesBefore.push(clone);
    }

    for (let i = 0; i < this.cloneCount; i++) {
      const clone = this.realSlides[i].cloneNode(true);
      clone.classList.add('icc-slide--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, [tabindex]').forEach(el => el.setAttribute('tabindex', '-1'));
      clonesAfter.push(clone);
    }

    // Prepend clones (before first real slide)
    clonesBefore.forEach(clone => {
      this.stage.insertBefore(clone, this.stage.firstChild);
    });

    // Append clones (after last real slide)
    clonesAfter.forEach(clone => {
      this.stage.appendChild(clone);
    });

    this.allSlides = [...this.stage.querySelectorAll('.icc-slide')];
  }

  _getCardWidth() {
    const vw = window.innerWidth;
    if (vw >= 990) {
      return Math.min(this.desktopCardWidth, vw * 0.45);
    } else if (vw >= 750) {
      return vw * 0.52;
    } else {
      return vw * (this.mobileCardWidthPct / 100);
    }
  }

  _setCardDimensions() {
    this.cardWidth = this._getCardWidth();
    const aspectNum = parseFloat(this.section.dataset.aspectRatio) || (5 / 4);
    this.cardHeight = this.cardWidth * aspectNum;

    // Stage height: card height only (label is now inside the image, no overflow below)
    const stageHeight = this.cardHeight;
    this.stage.style.height = stageHeight + 'px';
    this.viewport.style.height = (stageHeight + 30) + 'px'; // extra for box-shadow

    // Set each slide width
    this.allSlides.forEach(slide => {
      slide.style.width = this.cardWidth + 'px';
      slide.style.marginLeft = -(this.cardWidth / 2) + 'px';
    });
  }

  _getTransformForOffset(offset) {
    const abs = Math.abs(offset);
    const sign = offset < 0 ? -1 : (offset > 0 ? 1 : 0);
    const cw = this.cardWidth;
    const gap = this.cardGap;
    const ns = this.neighborScale;
    const nr = this.neighborRotation;
    const p = this.perspective;

    let scale, tx, tz, ry, opacity, zIndex;

    if (abs === 0) {
      scale = 1; tx = 0; tz = 0; ry = 0; opacity = 1; zIndex = 10;
    } else if (abs === 1) {
      scale = ns;
      tx = sign * (cw * 0.55 + gap);
      tz = -80;
      ry = -sign * nr;
      opacity = 1;
      zIndex = 8;
    } else if (abs === 2) {
      scale = ns * 0.84;
      tx = sign * (cw * 1.0 + gap * 2.2);
      tz = -160;
      ry = -sign * (nr * 1.7);
      opacity = 0.65;
      zIndex = 5;
    } else {
      scale = ns * 0.65;
      tx = sign * (cw * 1.5 + gap * 3);
      tz = -280;
      ry = -sign * (nr * 2.4);
      opacity = 0;
      zIndex = 1;
    }

    return {
      transform: `perspective(${p}px) translateX(${tx}px) translateZ(${tz}px) rotateY(${ry}deg) scale(${scale})`,
      opacity,
      zIndex
    };
  }

  _positionAll(animate) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const transition = (animate && !reducedMotion)
      ? `transform ${this.duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity ${this.duration}ms ease`
      : 'none';

    this.allSlides.forEach((slide, i) => {
      const offset = i - this.currentPos;
      const style = this._getTransformForOffset(offset);
      const isActive = i === this.currentPos;
      const isClone = slide.classList.contains('icc-slide--clone');

      slide.style.transition = transition;
      slide.style.transform = style.transform;
      slide.style.opacity = style.opacity;
      slide.style.zIndex = style.zIndex;
      slide.classList.toggle('icc-slide--active', isActive);

      // Pointer events only on nearby non-clone slides
      const isVisible = Math.abs(offset) <= 2;
      slide.style.pointerEvents = (isVisible && !isClone) ? 'auto' : 'none';

      // Aria
      if (isActive && !isClone) {
        slide.setAttribute('aria-hidden', 'false');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    this._updateDots();
  }

  _navigate(direction) {
    if (this.isTransitioning) return;

    const newPos = this.currentPos + direction;
    const max = this.N + 2 * this.cloneCount - 1;
    if (newPos < 0 || newPos > max) return;

    this.isTransitioning = true;
    this.currentPos = newPos;
    this._positionAll(true);
    this._resetAutoplay();

    setTimeout(() => {
      this._handleBoundary();
      this.isTransitioning = false;
    }, this.duration + 60);
  }

  _handleBoundary() {
    const low = this.cloneCount;
    const high = this.cloneCount + this.N - 1;

    if (this.currentPos < low) {
      this.currentPos += this.N;
      this._positionAll(false);
    } else if (this.currentPos > high) {
      this.currentPos -= this.N;
      this._positionAll(false);
    }
  }

  next() { this._navigate(1); }
  prev() { this._navigate(-1); }

  goTo(realIndex) {
    // realIndex is 0-based index into real slides
    const targetPos = realIndex + this.cloneCount;
    const diff = targetPos - this.currentPos;
    if (diff === 0) return;
    // Navigate step by step for short distances, or jump for large
    if (Math.abs(diff) <= 2) {
      this._navigate(Math.sign(diff));
    } else {
      // Jump directly without animation for large distances
      this.currentPos = targetPos;
      this._positionAll(false);
    }
  }

  _getCurrentRealIndex() {
    return this.currentPos - this.cloneCount;
  }

  // NAV ARROWS
  _buildNav() {
    const prevBtn = this.section.querySelector('.icc-nav__btn--prev');
    const nextBtn = this.section.querySelector('.icc-nav__btn--next');
    if (!prevBtn || !nextBtn) return;

    // If arrows disabled via setting, force-hide them regardless of CSS
    if (!this.showArrows) {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
      return;
    }

    prevBtn.addEventListener('click', () => this.prev());
    nextBtn.addEventListener('click', () => this.next());
  }

  // DOTS
  _buildDots() {
    const dotsContainer = this.section.querySelector('.icc-dots');
    if (!dotsContainer) return;
    dotsContainer.style.display = this.showDots ? 'flex' : 'none';
    if (!this.showDots) return;

    dotsContainer.innerHTML = '';
    for (let i = 0; i < this.N; i++) {
      const btn = document.createElement('button');
      btn.className = 'icc-dot';
      btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
      btn.addEventListener('click', () => this.goTo(i));
      dotsContainer.appendChild(btn);
    }
    this.dots = [...dotsContainer.querySelectorAll('.icc-dot')];
    this._updateDots();
  }

  _updateDots() {
    if (!this.dots || this.dots.length === 0) return;
    const ri = this._getCurrentRealIndex();
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('icc-dot--active', i === ri);
    });
  }

  // EVENTS
  _bindEvents() {
    // Touch
    this.stage.addEventListener('touchstart', this._onTouchStart, { passive: true });
    this.stage.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this.stage.addEventListener('touchend', this._onTouchEnd, { passive: true });
    this.stage.addEventListener('touchcancel', this._onTouchEnd, { passive: true });

    // Mouse drag
    this.stage.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._onMouseUp);

    // Click on slides
    this.stage.addEventListener('click', this._onClick);

    // Keyboard
    this.section.setAttribute('tabindex', '0');
    this.section.addEventListener('keydown', this._boundKeyDown);

    // Hover for autoplay pause
    if (this.pauseOnHover) {
      this.section.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this._stopAutoplay();
      });
      this.section.addEventListener('mouseleave', () => {
        this.isHovered = false;
        if (this.autoplayEnabled) this._startAutoplay();
      });
    }

    // Resize
    window.addEventListener('resize', this._boundHandleResize);
  }

  _unbindEvents() {
    this.stage.removeEventListener('touchstart', this._onTouchStart);
    this.stage.removeEventListener('touchmove', this._onTouchMove);
    this.stage.removeEventListener('touchend', this._onTouchEnd);
    this.stage.removeEventListener('touchcancel', this._onTouchEnd);
    this.stage.removeEventListener('mousedown', this._onMouseDown);
    document.removeEventListener('mousemove', this._onMouseMove);
    document.removeEventListener('mouseup', this._onMouseUp);
    this.stage.removeEventListener('click', this._onClick);
    this.section.removeEventListener('keydown', this._boundKeyDown);
    window.removeEventListener('resize', this._boundHandleResize);
    this._stopAutoplay();
  }

  // Touch handlers
  _onTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
    this.touchDelta = 0;
    this.isTouchHorizontal = null;
    this.isUserInteracting = true;
    this._stopAutoplay();
  }

  _onTouchMove(e) {
    const dx = e.touches[0].clientX - this.touchStartX;
    const dy = e.touches[0].clientY - this.touchStartY;

    if (this.isTouchHorizontal === null) {
      this.isTouchHorizontal = Math.abs(dx) > Math.abs(dy) * 1.2;
    }

    if (this.isTouchHorizontal) {
      e.preventDefault();
      this.touchDelta = dx;
    }
  }

  _onTouchEnd() {
    this.isUserInteracting = false;
    if (this.isTouchHorizontal && Math.abs(this.touchDelta) > 50) {
      if (this.touchDelta < 0) {
        this.next();
      } else {
        this.prev();
      }
    }
    this.touchDelta = 0;
    if (this.autoplayEnabled && !this.isHovered) {
      setTimeout(() => this._startAutoplay(), this.autoplayDelay);
    }
  }

  // Mouse drag handlers
  _onMouseDown(e) {
    if (e.button !== 0) return;
    this.dragStartX = e.clientX;
    this.dragDelta = 0;
    this.isDragging = true;
    this.isUserInteracting = true;
    this._stopAutoplay();
    this.stage.style.cursor = 'grabbing';
  }

  _onMouseMove(e) {
    if (!this.isDragging) return;
    this.dragDelta = e.clientX - this.dragStartX;
  }

  _onMouseUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.isUserInteracting = false;
    this.stage.style.cursor = '';

    if (Math.abs(this.dragDelta) > 50) {
      if (this.dragDelta < 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    if (this.autoplayEnabled && !this.isHovered) {
      setTimeout(() => this._startAutoplay(), this.autoplayDelay);
    }
  }

  _onClick(e) {
    // If user dragged, prevent click from firing
    if (Math.abs(this.dragDelta) > 10) {
      e.preventDefault();
      this.dragDelta = 0;
      return;
    }

    // Find which slide was clicked
    const clickedSlide = e.target.closest('.icc-slide');
    if (!clickedSlide) return;

    const idx = this.allSlides.indexOf(clickedSlide);
    const offset = idx - this.currentPos;

    if (offset === 0) {
      // Active slide: let the link work normally (do nothing, allow click)
      return;
    } else if (Math.abs(offset) <= 2) {
      e.preventDefault();
      this._navigate(Math.sign(offset));
    } else {
      e.preventDefault();
    }
  }

  _onKeyDown(e) {
    if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
    if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
  }

  // AUTOPLAY
  _startAutoplay() {
    if (!this.autoplayEnabled || this.isHovered || this.isUserInteracting) return;
    this._stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      if (!this.isHovered && !this.isUserInteracting) {
        this.next();
      }
    }, this.autoplayDelay);
  }

  _stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  _resetAutoplay() {
    this._stopAutoplay();
    if (this.autoplayEnabled && !this.isHovered) {
      this._startAutoplay();
    }
  }

  // RESIZE
  _onResize() {
    this._setCardDimensions();
    this._positionAll(false);
  }

  destroy() {
    this._unbindEvents();
    this.stage.querySelectorAll('.icc-slide--clone').forEach(el => el.remove());
  }

  // UTILITIES
  _debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }
}

// Registry to track instances per section
const ICCInstances = new Map();

function initICC(sectionEl) {
  const id = sectionEl.dataset.sectionId;
  if (ICCInstances.has(id)) {
    ICCInstances.get(id).destroy();
  }
  const instance = new ICCCarousel(sectionEl);
  ICCInstances.set(id, instance);
}

function destroyICC(sectionEl) {
  const id = sectionEl.dataset.sectionId;
  if (ICCInstances.has(id)) {
    ICCInstances.get(id).destroy();
    ICCInstances.delete(id);
  }
}

// Auto-init all sections on page load
document.querySelectorAll('.icc-section').forEach(initICC);

// Shopify Theme Editor events
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.icc-section');
  if (section) initICC(section);
});

document.addEventListener('shopify:section:unload', (e) => {
  const section = e.target.querySelector('.icc-section');
  if (section) destroyICC(section);
});

document.addEventListener('shopify:block:select', (e) => {
  const section = e.target.closest('.icc-section');
  if (!section) return;
  const id = section.dataset.sectionId;
  const instance = ICCInstances.get(id);
  if (!instance) return;
  // Find which real slide this block corresponds to
  const block = e.target;
  const slideIndex = [...section.querySelectorAll('.icc-slide[data-real="true"]')]
    .findIndex(s => s.dataset.blockId === block.dataset.blockId);
  if (slideIndex >= 0) instance.goTo(slideIndex);
});
